import React, { useState, useMemo, useEffect } from 'react';
import './Settings.css';
import { Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import authService from '../services/authService';
import { apiRequest } from '../services/apiInterceptor';
import Footer from './Footer';

// Predefined list of titles
const USER_TITLES = [
  'Mr.',
  'Mrs.',
  'Ms.',
  'Miss',
  'Mx.',
  'Dr.',
  'Professor',
  'Associate Professor',
  'Assistant Professor',
  'Adjunct Professor',
  'Lecturer',
  'Instructor',
  'Faculty',
  'Student Doctor',
  'Medical Student',
  'Pre-Med Student',
  'Resident Physician',
  'Resident',
  'Fellow',
  'Intern',
  'Physician',
  'Physician Assistant',
  'Nurse Practitioner',
  'Registered Nurse',
  'Clinical Pharmacist',
  'Paramedic',
  'EMT'
];

const mockUser = {
  name: "John Doe",
  email: "john@example.com"
};

const mockSessions = [
  { id: 1, device: "Chrome on Windows", lastActive: "2 hours ago" },
  { id: 2, device: "iPhone Safari", lastActive: "5 days ago" },
  { id: 3, device: "Android Chrome", lastActive: "1 week ago" }
];


export default function Settings() {
  const { isDarkMode, toggleTheme } = useTheme();
  const { user, getSessions, terminateSession, terminateAllOtherSessions, updateProfile } = useAuth();
  const { refreshSubscription } = useSubscription();
  const [profile, setProfile] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    title: user?.title || ''
  });
  const [password, setPassword] = useState({
    current: '',
    new: '',
    confirm: ''
  });
  const [showResetModal, setShowResetModal] = useState(false);
  const [agreeChecked, setAgreeChecked] = useState(false);
  const [isResettingData, setIsResettingData] = useState(false);
  const [resetMessage, setResetMessage] = useState({ type: '', text: '' });
  const [resetCompleted, setResetCompleted] = useState(false);

  // Delete account state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [deleteMessage, setDeleteMessage] = useState({ type: '', text: '' });
  const [sessions, setSessions] = useState([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(true);
  const [sessionsError, setSessionsError] = useState('');
  const [profileMessage, setProfileMessage] = useState({ type: '', text: '' });
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);

  // Subscription-related state
  const [subscriptions, setSubscriptions] = useState([]);
  const [subscribedPackages, setSubscribedPackages] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState('');
  const [isLoadingSubscriptions, setIsLoadingSubscriptions] = useState(true);
  const [subscriptionMessage, setSubscriptionMessage] = useState({ type: '', text: '' });
  const [isUpdatingSubscription, setIsUpdatingSubscription] = useState(false);

  // Email preferences state
  const [receivePromotions, setReceivePromotions] = useState(user?.receivePromotions ?? true);
  const [isUpdatingEmailPrefs, setIsUpdatingEmailPrefs] = useState(false);
  const [emailPrefsMessage, setEmailPrefsMessage] = useState({ type: '', text: '' });

  // Load sessions when component mounts
  useEffect(() => {
    const loadSessions = async () => {
      try {
        setIsLoadingSessions(true);
        setSessionsError('');
        const userSessions = await getSessions();
        setSessions(userSessions);
      } catch (error) {
        console.error('Failed to load sessions:', error);
        setSessionsError('Failed to load sessions');
      } finally {
        setIsLoadingSessions(false);
      }
    };

    loadSessions();
  }, [getSessions]);

  // Update profile when user changes
  useEffect(() => {
    if (user) {
      setProfile({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        title: user.title || ''
      });
      setReceivePromotions(user.receivePromotions ?? true);
    }
  }, [user]);

  // Load subscription data when component mounts
  useEffect(() => {
    const loadSubscriptions = async () => {
      try {
        setIsLoadingSubscriptions(true);
        
        // Fetch all user subscriptions
        const allSubscriptions = await apiRequest('/api/usersubscription').catch(() => []);
        // console.log('Raw subscriptions:', allSubscriptions);
        
        if (allSubscriptions && allSubscriptions.length > 0) {
          // Process all subscriptions to get their package information
          const subscriptionPackages = await Promise.all(
            allSubscriptions.map(async (sub) => {
              try {
                if (sub.planRateId) {
                  // Fetch the plan rate details to get package ID
                  const rateDetails = await apiRequest(`/api/plan/rate/${sub.planRateId}`);
                  const packageId = rateDetails.packageId || rateDetails.PackageId;
                  
                  if (packageId) {
                    // Fetch the package information
                    const packageInfo = await apiRequest(`/api/package/${packageId}`);
                    return {
                      subscription: sub,
                      package: packageInfo,
                      packageId: packageId
                    };
                  }
                }
                return {
                  subscription: sub,
                  package: null,
                  packageId: null
                };
              } catch (error) {
                console.warn('Error fetching package for subscription:', sub, error);
                return {
                  subscription: sub,
                  package: null,
                  packageId: null
                };
              }
            })
          );
          
          setSubscribedPackages(subscriptionPackages);
          setSubscriptions(allSubscriptions);
          
          // Find current subscription
          const current = allSubscriptions.find(sub => sub.current === true);
          setCurrentSubscription(current);
          
          // Set the default selected subscription to current one
          if (current) {
            setSelectedSubscriptionId(current.id || current.Id || '');
          }
          
          // console.log('Loaded subscription packages:', subscriptionPackages);
          // console.log('Current subscription:', current);
        } else {
          setSubscriptions([]);
          setSubscribedPackages([]);
          setCurrentSubscription(null);
        }
        
      } catch (error) {
        console.error('Failed to load subscription data:', error);
      } finally {
        setIsLoadingSubscriptions(false);
      }
    };

    if (user) {
      loadSubscriptions();
    }
  }, [user]);

  // Handle subscription plan switching
  const handleSetActivePlan = async () => {
    if (!selectedSubscriptionId) {
      setSubscriptionMessage({ type: 'error', text: 'Please select a subscription.' });
      return;
    }

    try {
      setIsUpdatingSubscription(true);
      setSubscriptionMessage({ type: '', text: '' });

      // Use the same API call structure as in Billing.js
      await apiRequest('/api/usersubscription/current', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(selectedSubscriptionId)
      });

      // Refresh subscription data
      const allSubscriptions = await apiRequest('/api/usersubscription').catch(() => []);
      setSubscriptions(allSubscriptions);
      
      // Find the updated current subscription
      const updatedCurrent = allSubscriptions.find(sub => sub.current === true);
      setCurrentSubscription(updatedCurrent);
      
      // Refresh global subscription context
      await refreshSubscription();

      setSubscriptionMessage({ 
        type: 'success', 
        text: 'Active plan updated successfully!' 
      });

      // Clear message after 5 seconds
      setTimeout(() => {
        setSubscriptionMessage({ type: '', text: '' });
      }, 5000);

    } catch (error) {
      console.error('Failed to update active plan:', error);
      setSubscriptionMessage({ 
        type: 'error', 
        text: error.message || 'Failed to update active plan. Please try again.' 
      });
    } finally {
      setIsUpdatingSubscription(false);
    }
  };

  const handleProfileChange = (e) => {
    setProfile({...profile, [e.target.name]: e.target.value});
  };

  const handlePasswordChange = (e) => {
    setPassword({...password, [e.target.name]: e.target.value});
  };

  const validatePassword = () => {
    if (password.new.length < 8) return "Password must be at least 8 characters";
    if (password.new !== password.confirm) return "Passwords do not match";
    return null;
  };

  const handlePasswordSubmit = async () => {
    try {
      const error = validatePassword();
      if (error) {
        alert(error);
        return;
      }

      await authService.changePassword(password.current, password.new);
      alert('Password updated successfully!');
      setPassword({ current: '', new: '', confirm: '' });
    } catch (error) {
      console.error('Password change error:', error);
      alert(error.message || 'Failed to update password. Please try again.');
    }
  };

  const handleProfileSubmit = async () => {
    try {
      setIsUpdatingProfile(true);
      setProfileMessage({ type: '', text: '' });

      await updateProfile({
        firstName: profile.firstName,
        lastName: profile.lastName,
        title: profile.title
      });

      setProfileMessage({ 
        type: 'success', 
        text: 'Profile updated successfully!' 
      });

      // Clear message after 5 seconds
      setTimeout(() => {
        setProfileMessage({ type: '', text: '' });
      }, 5000);

    } catch (error) {
      console.error('Profile update error:', error);
      setProfileMessage({ 
        type: 'error', 
        text: error.message || 'Failed to update profile. Please try again.' 
      });
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  const handlePromotionalEmailToggle = async (newValue) => {
    try {
      setIsUpdatingEmailPrefs(true);
      setEmailPrefsMessage({ type: '', text: '' });

      // Update the receivePromotions preference via API
      await apiRequest('/api/useraccount', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          receivePromotions: newValue
        })
      });

      setReceivePromotions(newValue);
      setEmailPrefsMessage({ 
        type: 'success', 
        text: newValue 
          ? 'You have subscribed to promotional emails.' 
          : 'You have unsubscribed from promotional emails.' 
      });

      // Clear message after 5 seconds
      setTimeout(() => {
        setEmailPrefsMessage({ type: '', text: '' });
      }, 5000);

    } catch (error) {
      console.error('Email preferences update error:', error);
      setEmailPrefsMessage({ 
        type: 'error', 
        text: error.message || 'Failed to update email preferences. Please try again.' 
      });
    } finally {
      setIsUpdatingEmailPrefs(false);
    }
  };

  const handleKickSession = async (sessionId) => {
    try {
      await terminateSession(sessionId);
      setSessions(sessions.filter(session => session.id !== sessionId));
    } catch (error) {
      console.error('Failed to terminate session:', error);
      alert('Failed to terminate session. Please try again.');
    }
  };

  const handleKickAll = async () => {
    try {
      await terminateAllOtherSessions();
      // Keep only the current session (first one in the list, typically)
      setSessions(sessions.slice(0, 1));
    } catch (error) {
      console.error('Failed to terminate sessions:', error);
      alert('Failed to terminate other sessions. Please try again.');
    }
  };

  const handleResetAllData = async () => {
    if (!agreeChecked) {
      return;
    }

    try {
      setIsResettingData(true);
      setResetMessage({ type: '', text: '' });

      // Just await the API request - if it succeeds, we'll reach the success code
      await apiRequest('/api/useraccount/reset-all-data', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      // If we reach this point, the API call was successful
      setResetMessage({ 
        type: 'success', 
        text: 'All user data has been successfully reset. You can now start fresh!' 
      });
      setResetCompleted(true);
      
      // Optional: You might want to refresh the page or redirect the user
      // window.location.reload();
    } catch (error) {
      console.error('Failed to reset user data:', error);
      setResetMessage({ 
        type: 'error', 
        text: 'Failed to reset user data. Please try again later.' 
      });
      setResetCompleted(false); // Ensure we stay in confirmation mode on error
    } finally {
      setIsResettingData(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText.toLowerCase() !== 'delete') return;
    try {
      setIsDeletingAccount(true);
      setDeleteMessage({ type: '', text: '' });
      await apiRequest('/api/useraccount', { method: 'DELETE' });
      // On success, log out and redirect to login
      await authService.logout();
      window.location.href = '/login';
    } catch (error) {
      console.error('Failed to delete account:', error);
      setDeleteMessage({ type: 'error', text: error.message || 'Failed to delete account. Please try again.' });
    } finally {
      setIsDeletingAccount(false);
    }
  };

  // Theme-specific styles
  const themeStyles = useMemo(() => {    return {
      container: {
        background: isDarkMode
          ? 'linear-gradient(180deg, #0e1626 0%, #0b1220 100%)'
          : '#eceff4'
      },
      text: {
        primary: isDarkMode ? '#ffffff' : '#2c3e50',
        secondary: isDarkMode ? '#e1e9ff' : 'rgba(44, 62, 80, 0.8)'
      },
      content: {
        background: isDarkMode ? 'rgba(15, 32, 54, 0.7)' : 'rgba(255, 255, 255, 0.9)',
        shadow: isDarkMode ? '0 4px 10px rgba(0, 0, 0, 0.3)' : '0 4px 10px rgba(0, 0, 0, 0.1)'
      },
      input: {
        background: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#ffffff',
        color: isDarkMode ? '#ffffff' : '#2c3e50',
        border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid #dcdfe6'
      },
      button: {
        primary: {
          background: isDarkMode ? '#0074cc' : '#1976d2',
          color: '#ffffff',
          border: 'none'
        },
        warning: {
          background: 'transparent',
          color: isDarkMode ? '#ffb3b3' : '#e53e3e',
          border: isDarkMode ? '1px solid #e53e3e' : '1px solid #e53e3e'
        }
      },
      modal: {
        background: isDarkMode ? '#151e30' : '#ffffff',
        color: isDarkMode ? '#ffffff' : '#2c3e50',
        border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e8ebf2'
      }
    };
  }, [isDarkMode]);

  return (
    <div className="settings-page" data-theme={isDarkMode ? 'dark' : 'light'}>
      <div className="settings-container">
        {/* Header */}
        <div className="settings-header">
          <div className="header-content">
            <h1 className="page-title">Account Settings</h1>
            <p className="page-subtitle">Manage your account preferences and security</p>
          </div>
        </div>

        <div className="settings-content">
          {/* Left Sidebar Navigation */}
          <div className="settings-sidebar">
            <nav className="settings-nav">
              <a href="#profile" className="nav-item active">
                <div className="nav-icon">👤</div>
                <span>Profile</span>
              </a>
              <a href="#subscription" className="nav-item">
                <div className="nav-icon">💳</div>
                <span>Active Plan</span>
              </a>
              <a href="#security" className="nav-item">
                <div className="nav-icon">🔐</div>
                <span>Security</span>
              </a>
              <a href="#preferences" className="nav-item">
                <div className="nav-icon">⚙️</div>
                <span>Preferences</span>
              </a>
              <a href="#email-preferences" className="nav-item">
                <div className="nav-icon">📧</div>
                <span>Email</span>
              </a>
              <a href="#sessions" className="nav-item">
                <div className="nav-icon">📱</div>
                <span>Sessions</span>
              </a>
              <a href="#data" className="nav-item">
                <div className="nav-icon">🗂️</div>
                <span>Data</span>
              </a>
              <a href="#delete-account" className="nav-item">
                <div className="nav-icon">🗑️</div>
                <span>Delete Account</span>
              </a>
            </nav>
          </div>

          {/* Main Content Area */}
          <div className="settings-main">
            {/* Profile Section */}
            <section id="profile" className="settings-card">
              <div className="card-header">
                <h2 className="card-title">Profile Information</h2>
                <p className="card-description">Update your personal details and preferences</p>
              </div>
              
              {profileMessage.text && (
                <div className={`alert alert-${profileMessage.type}`}>
                  <div className="alert-icon">
                    {profileMessage.type === 'success' ? '✅' : '❌'}
                  </div>
                  <span>{profileMessage.text}</span>
                </div>
              )}

              <div className="card-content">
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">First Name</label>
                    <input 
                      type="text" 
                      name="firstName"
                      value={profile.firstName}
                      onChange={handleProfileChange}
                      disabled={isUpdatingProfile}
                      className="form-input"
                      placeholder="Enter your first name"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">Last Name</label>
                    <input 
                      type="text" 
                      name="lastName"
                      value={profile.lastName}
                      onChange={handleProfileChange}
                      disabled={isUpdatingProfile}
                      className="form-input"
                      placeholder="Enter your last name"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">Professional Title</label>
                    <select
                      name="title"
                      value={profile.title}
                      onChange={handleProfileChange}
                      disabled={isUpdatingProfile}
                      className="form-select"
                    >
                      <option value="">Select your title</option>
                      {USER_TITLES.map((title) => (
                        <option key={title} value={title}>
                          {title}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">Email Address</label>
                    <input
                      type="email"
                      name="email"
                      value={profile.email}
                      disabled={true}
                      className="form-input disabled"
                    />
                    <small className="form-hint">Email cannot be changed for security reasons</small>
                  </div>
                </div>
                
                <div className="card-actions">
                  <button 
                    className="btn btn-primary"
                    onClick={handleProfileSubmit}
                    disabled={isUpdatingProfile}
                  >
                    {isUpdatingProfile ? (
                      <>
                        <span className="spinner"></span>
                        Updating...
                      </>
                    ) : (
                      'Save Changes'
                    )}
                  </button>
                </div>
              </div>
            </section>

            {/* Subscription Management Section */}
            <section id="subscription" className="settings-card">
              <div className="card-header">
                <h2 className="card-title">Active Plan</h2>
                <p className="card-description">Manage your subscription and billing</p>
              </div>
              
              {subscriptionMessage.text && (
                <div className={`alert alert-${subscriptionMessage.type}`}>
                  <div className="alert-icon">
                    {subscriptionMessage.type === 'success' ? '✅' : '❌'}
                  </div>
                  <span>{subscriptionMessage.text}</span>
                </div>
              )}

              <div className="card-content">
                {isLoadingSubscriptions ? (
                  <div className="loading-state">
                    <span className="spinner"></span>
                    <span>Loading subscription data...</span>
                  </div>
                ) : (
                  <>
                    {/* Current Active Plan Display */}
                    {currentSubscription && (
                      <div className="current-plan-info">
                        <strong>
                          {(() => {
                            // Find the package info for the current subscription
                            const subPackage = subscribedPackages.find(sp =>
                              sp.subscription &&
                              (sp.subscription.id || sp.subscription.Id) === (currentSubscription.id || currentSubscription.Id)
                            );
                            const packageName = subPackage?.package?.name || subPackage?.package?.Name || 'Unknown Package';
                            return `Current Active Plan: ${packageName}`;
                          })()}
                        </strong>
                      </div>
                    )}

                    {/* Plan Selection - only show if multiple active subscriptions */}
                    {subscriptions.filter(sub => sub.status && sub.status.toLowerCase() === 'active').length > 1 && (
                      <div className="subscription-management">
                        <h3 className="section-subtitle">Switch Active Plan</h3>
                        <p className="section-description">
                          You have multiple subscriptions. Select which one to use as your active plan.
                        </p>
                        
                        <div className="form-grid">
                          <div className="form-group">
                            <label className="form-label">Select Plan</label>
                            <select
                              value={selectedSubscriptionId}
                              onChange={(e) => setSelectedSubscriptionId(e.target.value)}
                              className="form-select"
                              disabled={isUpdatingSubscription}
                            >
                              {subscriptions
                                .filter(sub => sub.status && sub.status.toLowerCase() === 'active')
                                .map((subscription) => {
                                  // Find the package info for this subscription
                                  const subPackage = subscribedPackages.find(sp => 
                                    sp.subscription && 
                                    (sp.subscription.id || sp.subscription.Id) === (subscription.id || subscription.Id)
                                  );
                                  
                                  const packageName = subPackage?.package?.name || subPackage?.package?.Name || 'Unknown Package';
                                  const isActive = currentSubscription && 
                                    (subscription.id || subscription.Id) === (currentSubscription.id || currentSubscription.Id);
                                  
                                  return (
                                    <option 
                                      key={subscription.id || subscription.Id} 
                                      value={subscription.id || subscription.Id}
                                    >
                                      {packageName} {isActive ? '- Currently Active' : ''}
                                    </option>
                                  );
                                })}
                            </select>
                          </div>
                        </div>
                        
                        <div className="card-actions">
                          <button 
                            className="btn btn-primary"
                            onClick={handleSetActivePlan}
                            disabled={isUpdatingSubscription || !selectedSubscriptionId || 
                              (currentSubscription && selectedSubscriptionId === (currentSubscription.id || currentSubscription.Id).toString())}
                          >
                            {isUpdatingSubscription ? (
                              <>
                                <span className="spinner"></span>
                                Updating...
                              </>
                            ) : (
                              'Set as Active Plan'
                            )}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* No subscriptions state */}
                    {subscriptions.length === 0 && (
                      <div className="no-subscriptions">
                        <div className="empty-state">
                          <div className="empty-icon">💳</div>
                          <h3>No Active Subscriptions</h3>
                          <p>You don't have any active subscriptions. Visit the billing page to get started.</p>
                          <Link to="/billing" className="btn btn-primary">
                            View Plans
                          </Link>
                        </div>
                      </div>
                    )}

                    {/* Single subscription or only one active subscription display */}
                    {(subscriptions.filter(sub => sub.status && sub.status.toLowerCase() === 'active').length === 1 || 
                      (subscriptions.length === 1)) && (
                      <div className="single-subscription">
                        {(() => {
                          const activeSubscription = subscriptions.find(sub => 
                            sub.status && sub.status.toLowerCase() === 'active'
                          ) || subscriptions[0];
                          
                          const subPackage = subscribedPackages.find(sp => 
                            sp.subscription && 
                            (sp.subscription.id || sp.subscription.Id) === (activeSubscription?.id || activeSubscription?.Id)
                          );
                          
                          const packageName = subPackage?.package?.name || subPackage?.package?.Name || 'Unknown Package';
                          
                          return (
                            <div className="current-plan-info">
                              <h4>Current Plan: {packageName} - Currently Active</h4>
                              <p className="info-text">
                                You have one active subscription. To add more plans or change your subscription, 
                                visit the billing page.
                              </p>
                            </div>
                          );
                        })()}
                        <div className="card-actions">
                          <Link to="/billing" className="btn btn-secondary">
                            Manage Billing
                          </Link>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </section>

            {/* Theme Preferences */}
            <section id="preferences" className="settings-card">
              <div className="card-header">
                <h2 className="card-title">Display Preferences</h2>
                <p className="card-description">Customize your viewing experience</p>
              </div>
              
              <div className="card-content">
                <div className="preference-item">
                  <div className="preference-info">
                    <h3 className="preference-title">Dark Mode</h3>
                    <p className="preference-description">Switch between light and dark themes</p>
                  </div>
                  <div className="preference-control">
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={isDarkMode}
                        onChange={toggleTheme}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                </div>
              </div>
            </section>

            {/* Email Preferences */}
            <section id="email-preferences" className="settings-card">
              <div className="card-header">
                <h2 className="card-title">Email Preferences</h2>
                <p className="card-description">Manage your email communication preferences</p>
              </div>
              
              {emailPrefsMessage.text && (
                <div className={`alert alert-${emailPrefsMessage.type}`}>
                  <div className="alert-icon">
                    {emailPrefsMessage.type === 'success' ? '✅' : '❌'}
                  </div>
                  <span>{emailPrefsMessage.text}</span>
                </div>
              )}
              
              <div className="card-content">
                <div className="preference-item">
                  <div className="preference-info">
                    <h3 className="preference-title">Promotional Emails</h3>
                    <p className="preference-description">
                      Receive updates on the latest products and promotions via email or other channels
                    </p>
                  </div>
                  <div className="preference-control">
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={receivePromotions}
                        onChange={(e) => handlePromotionalEmailToggle(e.target.checked)}
                        disabled={isUpdatingEmailPrefs}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                </div>
                {isUpdatingEmailPrefs && (
                  <div className="loading-state" style={{ marginTop: '10px' }}>
                    <span className="spinner"></span>
                    <span>Updating preferences...</span>
                  </div>
                )}
              </div>
            </section>

            {/* Security Section */}
            <section id="security" className="settings-card">
              <div className="card-header">
                <h2 className="card-title">Security & Privacy</h2>
                <p className="card-description">
                  {user?.auth === 'Email' 
                    ? 'Manage your password and account security'
                    : 'Manage your account security settings'}
                </p>
              </div>
              
              <div className="card-content">
                {/* Only show password management for Email auth users */}
                {user?.auth === 'Email' ? (
                  <>
                    <div className="form-grid">
                      <div className="form-group">
                        <label className="form-label">Current Password</label>
                        <input
                          type="password"
                          name="current"
                          value={password.current}
                          onChange={handlePasswordChange}
                          className="form-input"
                          placeholder="Enter current password"
                        />
                      </div>
                      
                      <div className="form-group">
                        <label className="form-label">New Password</label>
                        <input
                          type="password"
                          name="new"
                          value={password.new}
                          onChange={handlePasswordChange}
                          className="form-input"
                          placeholder="Enter new password"
                        />
                      </div>
                      
                      <div className="form-group">
                        <label className="form-label">Confirm New Password</label>
                        <input
                          type="password"
                          name="confirm"
                          value={password.confirm}
                          onChange={handlePasswordChange}
                          className="form-input"
                          placeholder="Confirm new password"
                        />
                      </div>
                    </div>
                    
                    <div className="security-tips">
                      <h4 className="tips-title">Password Requirements:</h4>
                      <ul className="tips-list">
                        <li className={password.new.length >= 8 ? 'valid' : ''}>At least 8 characters</li>
                        <li className={password.new !== password.confirm ? '' : 'valid'}>Passwords must match</li>
                      </ul>
                    </div>
                    
                    <div className="card-actions">
                      <button 
                        className="btn btn-primary"
                        onClick={handlePasswordSubmit}
                      >
                        Update Password
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="oauth-notice">
                    <div className="oauth-notice-icon">🔐</div>
                    <div className="oauth-notice-content">
                      <h4 className="oauth-notice-title">Signed in with {user?.auth}</h4>
                      <p className="oauth-notice-text">
                        Your account is linked to {user?.auth}. Password management is handled through your {user?.auth} account settings.
                      </p>
                      <p className="oauth-notice-hint">
                        To change your password, please visit your {user?.auth} account security settings.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </section>

            {/* Active Sessions */}
            <section id="sessions" className="settings-card">
              <div className="card-header">
                <h2 className="card-title">Active Sessions</h2>
                <p className="card-description">Monitor and manage your device sessions</p>
              </div>
              
              <div className="card-content">
                {isLoadingSessions ? (
                  <div className="loading-state">
                    <div className="loading-spinner"></div>
                    <span>Loading sessions...</span>
                  </div>
                ) : sessionsError ? (
                  <div className="error-state">
                    <div className="error-icon">⚠️</div>
                    <span>{sessionsError}</span>
                  </div>
                ) : sessions.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-icon">📱</div>
                    <span>No active sessions found</span>
                  </div>
                ) : (
                  <>
                    <div className="sessions-grid">
                      {sessions.map(session => (
                        <div key={session.id} className={`session-card ${session.isCurrent ? 'current' : ''}`}>
                          <div className="session-info">
                            <div className="session-device">
                              <span className="device-icon">
                                {session.deviceInfo?.includes('iPhone') || session.deviceInfo?.includes('Android') ? '📱' : '💻'}
                              </span>
                              <div className="device-details">
                                <h4 className="device-name">
                                  {session.deviceInfo || session.userAgent || 'Unknown Device'}
                                </h4>
                                <p className="device-time">
                                  {session.lastActive ? new Date(session.lastActive).toLocaleString() : 'Recently active'}
                                </p>
                                {session.ipAddress && (
                                  <p className="device-ip">IP: {session.ipAddress}</p>
                                )}
                              </div>
                            </div>
                            
                            {session.isCurrent ? (
                              <div className="session-badge current-badge">Current Session</div>
                            ) : (
                              <button 
                                className="btn btn-secondary btn-sm"
                                onClick={() => handleKickSession(session.id)}
                              >
                                Sign Out
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {sessions.length > 1 && (
                      <div className="card-actions">
                        <button 
                          className="btn btn-warning"
                          onClick={handleKickAll}
                        >
                          Sign Out All Other Sessions
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </section>

            {/* Notification Preferences */}
            <section className="settings-card">
              <div className="card-header">
                <h2 className="card-title">Notification Preferences</h2>
                <p className="card-description">Control how you receive updates and reminders</p>
              </div>
              
              <div className="card-content">
                <div className="notification-grid">
                  <div className="notification-item disabled">
                    <div className="notification-info">
                      <h4 className="notification-title">Practice Reminders</h4>
                      <p className="notification-description">Get notified about scheduled practice sessions</p>
                    </div>
                    <label className="toggle-switch disabled">
                      <input type="checkbox" disabled />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                  
                  <div className="notification-item disabled">
                    <div className="notification-info">
                      <h4 className="notification-title">Exam Notifications</h4>
                      <p className="notification-description">Receive updates about simulated exams</p>
                    </div>
                    <label className="toggle-switch disabled">
                      <input type="checkbox" disabled />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                  
                  <div className="notification-item disabled">
                    <div className="notification-info">
                      <h4 className="notification-title">Performance Insights</h4>
                      <p className="notification-description">Get insights about your study progress</p>
                    </div>
                    <label className="toggle-switch disabled">
                      <input type="checkbox" disabled />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                </div>
                
                <div className="feature-notice">
                  <div className="notice-icon">🚧</div>
                  <div className="notice-content">
                    <h4>Coming Soon</h4>
                    <p>Notification preferences will be available in a future update.</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Data Management */}
            <section id="data" className="settings-card danger-card">              <div className="card-header">
                <h2 className="card-title">Data Management</h2>
                <p className="card-description">Manage your performance data and account information</p>
              </div>
              
              {resetMessage.text && (
                <div className={`alert alert-${resetMessage.type}`}>
                  <div className="alert-icon">
                    {resetMessage.type === 'success' ? '✅' : '❌'}
                  </div>
                  <span>{resetMessage.text}</span>
                </div>
              )}
              
              <div className="card-content">
                <div className="danger-zone">
                  <div className="danger-header">
                    <div className="danger-icon">⚠️</div>
                    <div className="danger-info">
                      <h4 className="danger-title">Reset Performance Data</h4>
                      <p className="danger-description">
                        This will permanently delete all your performance history, 
                        test results, and reset your statistics. This action cannot be undone.
                      </p>
                    </div>
                  </div>
                  
                  <div className="card-actions">
                    <button 
                      className="btn btn-danger"
                      style={{
                        background: '#e53e3e',
                        color: '#fff',
                        border: 'none',
                        fontWeight: 600,
                        fontSize: '1rem',
                        borderRadius: '8px',
                        padding: '12px 24px',
                        boxShadow: '0 2px 8px rgba(229,62,62,0.15)',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        outline: 'none',
                      }}
                      onClick={() => {
                        setShowResetModal(true);
                        setResetMessage({ type: '', text: '' }); // Clear any previous messages
                        setResetCompleted(false); // Reset completion state
                      }}
                    >
                      Reset All Data
                    </button>
                  </div>
                </div>
              </div>
            </section>

            {/* Delete Account */}
            <section id="delete-account" className="settings-card danger-card">
              <div className="card-header">
                <h2 className="card-title">Delete Account</h2>
                <p className="card-description">Permanently delete your account and all associated data</p>
              </div>

              {deleteMessage.text && (
                <div className={`alert alert-${deleteMessage.type}`}>
                  <div className="alert-icon">{deleteMessage.type === 'success' ? '✅' : '❌'}</div>
                  <span>{deleteMessage.text}</span>
                </div>
              )}

              <div className="card-content">
                <div className="danger-zone">
                  <div className="danger-header">
                    <div className="danger-icon">⛔</div>
                    <div className="danger-info">
                      <h4 className="danger-title">Permanently Delete Account</h4>
                      <p className="danger-description">
                        This will permanently delete your account, all your data, subscriptions, and
                        performance history. <strong>This action cannot be undone.</strong>
                      </p>
                    </div>
                  </div>

                  <div className="card-actions">
                    <button
                      className="btn btn-danger"
                      style={{ background: '#e53e3e', color: '#fff', border: 'none', fontWeight: 600, fontSize: '1rem', borderRadius: '8px', padding: '12px 24px', boxShadow: '0 2px 8px rgba(229,62,62,0.15)', cursor: 'pointer', transition: 'all 0.2s', outline: 'none' }}
                      onClick={() => { setShowDeleteModal(true); setDeleteMessage({ type: '', text: '' }); setDeleteConfirmText(''); }}
                    >
                      Delete Account
                    </button>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>

        {/* Delete Account Confirmation Modal */}
        {showDeleteModal && (
          <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }}>
            <div className="modal-container" style={{ background: isDarkMode ? '#2d3748' : '#ffffff', borderRadius: '16px', maxWidth: '95vw', minWidth: '340px', width: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.25)', display: 'flex', flexDirection: 'column', overflow: 'visible', paddingBottom: 0, color: isDarkMode ? '#e2e8f0' : '#2d3748' }}>
              <div className="modal-header" style={{ padding: '24px 24px 0 24px' }}>
                <h3 className="modal-title" style={{ margin: 0, color: isDarkMode ? '#e2e8f0' : '#2d3748', fontSize: '1.5rem', fontWeight: '600' }}>
                  Delete Account
                </h3>
              </div>
              <div className="modal-content" style={{ padding: '16px 24px', overflow: 'visible' }}>
                {deleteMessage.text && deleteMessage.type === 'error' && (
                  <div style={{ background: isDarkMode ? '#742a2a' : '#fef2f2', border: `1px solid ${isDarkMode ? '#c53030' : '#fecaca'}`, borderRadius: '6px', padding: '12px', marginBottom: '16px', color: isDarkMode ? '#fed7d7' : '#dc2626' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <span style={{ marginRight: '8px' }}>❌</span>
                      <span>{deleteMessage.text}</span>
                    </div>
                  </div>
                )}
                <div style={{ marginBottom: '12px', color: isDarkMode ? '#e2e8f0' : '#2d3748', lineHeight: '1.5' }}>
                  <p>This will <strong>permanently delete</strong> your account, including:</p>
                  <ul style={{ margin: '8px 0 12px 20px', color: isDarkMode ? '#e2e8f0' : '#2d3748', lineHeight: '1.4' }}>
                    <li>Your profile and all personal data</li>
                    <li>All performance history and test results</li>
                    <li>Active subscriptions and billing information</li>
                    <li>Saved questions and folders</li>
                  </ul>
                  <p style={{ color: isDarkMode ? '#fed7d7' : '#dc2626', fontWeight: '600' }}>This action cannot be undone.</p>
                </div>
                <p style={{ marginBottom: '8px', color: isDarkMode ? '#e2e8f0' : '#2d3748', fontSize: '14px', fontWeight: '500' }}>
                  Type <strong>delete</strong> to confirm:
                </p>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={e => setDeleteConfirmText(e.target.value)}
                  placeholder="delete"
                  disabled={isDeletingAccount}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.2)' : '#e2e8f0'}`, background: isDarkMode ? 'rgba(255,255,255,0.08)' : '#f7fafc', color: isDarkMode ? '#ffffff' : '#2d3748', fontSize: '16px', outline: 'none', boxSizing: 'border-box' }}
                />
              </div>
              <div className="modal-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', padding: '16px 24px 24px 24px' }}>
                <button
                  disabled={isDeletingAccount}
                  onClick={() => { setShowDeleteModal(false); setDeleteConfirmText(''); setDeleteMessage({ type: '', text: '' }); }}
                  style={{ background: isDarkMode ? '#4a5568' : '#e2e8f0', color: isDarkMode ? '#e2e8f0' : '#4a5568', border: 'none', padding: '12px 20px', borderRadius: '8px', fontWeight: '500', fontSize: '1rem', cursor: isDeletingAccount ? 'not-allowed' : 'pointer', opacity: isDeletingAccount ? 0.6 : 1 }}
                >
                  Cancel
                </button>
                <button
                  disabled={deleteConfirmText.toLowerCase() !== 'delete' || isDeletingAccount}
                  onClick={handleDeleteAccount}
                  style={{ background: (deleteConfirmText.toLowerCase() === 'delete' && !isDeletingAccount) ? '#e53e3e' : (isDarkMode ? '#4a5568' : '#f8d7da'), color: (deleteConfirmText.toLowerCase() === 'delete' && !isDeletingAccount) ? '#ffffff' : (isDarkMode ? '#a0aec0' : '#e53e3e'), border: 'none', padding: '12px 20px', borderRadius: '8px', fontWeight: '600', fontSize: '1rem', cursor: (deleteConfirmText.toLowerCase() !== 'delete' || isDeletingAccount) ? 'not-allowed' : 'pointer', opacity: (deleteConfirmText.toLowerCase() !== 'delete' || isDeletingAccount) ? 0.7 : 1 }}
                >
                  {isDeletingAccount ? (
                    <><span className="spinner" style={{ marginRight: '8px' }}></span>Deleting...</>
                  ) : 'Delete My Account'}
                </button>
              </div>
            </div>
          </div>
        )}
        {showResetModal && (
          <div className="modal-overlay" style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0,0,0,0.6)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(4px)'
          }}>
            <div className="modal-container" style={{
              background: isDarkMode ? '#2d3748' : '#ffffff',
              borderRadius: '16px',
              maxWidth: '95vw',
              minWidth: '340px',
              width: 'auto',
              boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'visible',
              paddingBottom: 0,
              color: isDarkMode ? '#e2e8f0' : '#2d3748'
            }}>
              <div className="modal-header" style={{
                padding: '24px 24px 0 24px',
                flex: '0 0 auto',
              }}>
                <h3 className="modal-title" style={{
                  margin: 0, 
                  wordBreak: 'break-word',
                  color: isDarkMode ? '#e2e8f0' : '#2d3748',
                  fontSize: '1.5rem',
                  fontWeight: '600'
                }}>
                  {resetCompleted ? 'Data Reset Complete' : 'Confirm Data Reset'}
                </h3>
              </div>
              <div className="modal-content" style={{
                padding: '16px 24px',
                overflow: 'visible',
                wordBreak: 'break-word',
                whiteSpace: 'normal',
              }}>
                {/* Show error message if there's one */}
                {resetMessage.text && resetMessage.type === 'error' && (
                  <div style={{
                    background: isDarkMode ? '#742a2a' : '#fef2f2',
                    border: `1px solid ${isDarkMode ? '#c53030' : '#fecaca'}`,
                    borderRadius: '6px',
                    padding: '12px',
                    marginBottom: '16px',
                    color: isDarkMode ? '#fed7d7' : '#dc2626'
                  }}>
                    <div style={{display: 'flex', alignItems: 'center'}}>
                      <span style={{marginRight: '8px'}}>❌</span>
                      <span>{resetMessage.text}</span>
                    </div>
                  </div>
                )}

                {resetCompleted ? (
                  // Success content
                  <div className="modal-success">
                    <div className="success-icon" style={{fontSize: '3rem', marginBottom: 16, textAlign: 'center'}}>✅</div>
                    <div style={{textAlign: 'center'}}>
                      <h4 style={{
                        margin: '0 0 12px 0', 
                        color: isDarkMode ? '#68d391' : '#16a085',
                        fontSize: '1.3rem',
                        fontWeight: '600'
                      }}>Success!</h4>
                      <p style={{
                        marginBottom: 16,
                        color: isDarkMode ? '#e2e8f0' : '#2d3748',
                        lineHeight: '1.5'
                      }}>
                        All your performance data has been successfully reset. You can now start fresh with a clean slate.
                      </p>
                      <p style={{
                        marginBottom: 0, 
                        fontSize: '0.9em', 
                        color: isDarkMode ? '#a0aec0' : '#666',
                        lineHeight: '1.4'
                      }}>
                        Your account remains active and you can continue using the application.
                      </p>
                    </div>
                  </div>
                ) : (
                  // Confirmation content
                  <div className="modal-warning">
                    <div className="warning-icon" style={{fontSize: '2rem', marginBottom: 8}}>⚠️</div>
                    <p style={{
                      marginBottom: 12,
                      color: isDarkMode ? '#e2e8f0' : '#2d3748',
                      lineHeight: '1.5'
                    }}>
                      Are you absolutely sure you want to reset all your performance data?
                      <br />This action will permanently delete:
                    </p>
                    <ul style={{
                      margin: '12px 0', 
                      paddingLeft: 20,
                      color: isDarkMode ? '#e2e8f0' : '#2d3748',
                      lineHeight: '1.4'
                    }}>
                      <li>All test results and scores</li>
                      <li>Performance analytics and insights</li>
                      <li>Study progress and statistics</li>
                      <li>Question attempt history</li>
                      <li>Question bookmarks and rates</li>
                    </ul>
                    <p style={{
                      marginBottom: 0,
                      color: isDarkMode ? '#fed7d7' : '#dc2626',
                      fontWeight: '600'
                    }}>
                      <strong>This action cannot be undone.</strong>
                    </p>
                  </div>
                )}
                
                {!resetCompleted && (
                  <div style={{
                    marginTop: 18,
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%'
                  }}>
                    <input
                      type="checkbox"
                      id="agree-reset"
                      checked={agreeChecked}
                      onChange={e => setAgreeChecked(e.target.checked)}
                      style={{marginRight: 8}}
                    />
                    <label htmlFor="agree-reset" style={{
                      userSelect: 'none', 
                      cursor: 'pointer',
                      color: isDarkMode ? '#e2e8f0' : '#2d3748',
                      fontWeight: '500'
                    }}>I Agree</label>
                  </div>
                )}
              </div>
              <div className="modal-actions" style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '12px',
                padding: '16px 24px 24px 24px',
                flex: '0 0 auto',
                background: 'inherit',
              }}>
                {resetCompleted ? (
                  // Success state - only show Close button
                  <button 
                    className="btn btn-primary"
                    onClick={() => {
                      setShowResetModal(false);
                      setAgreeChecked(false);
                      setResetCompleted(false);
                      setResetMessage({ type: '', text: '' });
                    }}
                    style={{
                      background: isDarkMode ? '#38b2ac' : '#16a085',
                      color: '#ffffff',
                      border: 'none',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      fontWeight: '600',
                      fontSize: '1rem',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 2px 8px rgba(22, 160, 133, 0.2)'
                    }}
                  >
                    Close
                  </button>
                ) : (
                  // Confirmation state - show Cancel and Reset buttons
                  <>
                    <button 
                      className="btn btn-secondary"
                      disabled={isResettingData}
                      onClick={() => {
                        setShowResetModal(false);
                        setAgreeChecked(false);
                        setResetCompleted(false);
                        setResetMessage({ type: '', text: '' });
                      }}
                      style={{
                        background: isDarkMode ? '#4a5568' : '#e2e8f0',
                        color: isDarkMode ? '#e2e8f0' : '#4a5568',
                        border: 'none',
                        padding: '12px 20px',
                        borderRadius: '8px',
                        fontWeight: '500',
                        fontSize: '1rem',
                        cursor: isResettingData ? 'not-allowed' : 'pointer',
                        opacity: isResettingData ? 0.6 : 1,
                        transition: 'all 0.2s ease'
                      }}
                    >
                      Cancel
                    </button>
                    <button 
                      className="btn btn-danger"
                      disabled={!agreeChecked || isResettingData}
                      style={{
                        background: (agreeChecked && !isResettingData) ? '#e53e3e' : (isDarkMode ? '#4a5568' : '#f8d7da'),
                        color: (agreeChecked && !isResettingData) ? '#ffffff' : (isDarkMode ? '#a0aec0' : '#e53e3e'),
                        border: 'none',
                        padding: '12px 20px',
                        borderRadius: '8px',
                        fontWeight: '600',
                        fontSize: '1rem',
                        cursor: (!agreeChecked || isResettingData) ? 'not-allowed' : 'pointer',
                        opacity: (!agreeChecked || isResettingData) ? 0.7 : 1,
                        transition: 'all 0.2s ease',
                        boxShadow: (agreeChecked && !isResettingData) ? '0 2px 8px rgba(229,62,62,0.2)' : 'none'
                      }}
                      onClick={handleResetAllData}
                    >
                      {isResettingData ? (
                        <>
                          <span className="spinner" style={{marginRight: '8px'}}></span>
                          Resetting...
                        </>
                      ) : (
                        'Yes, Reset All Data'
                      )}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      
      <Footer />
    </div>
  );
}
